import {
  Stack,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { useParams } from "react-router-dom";
import { QuestionSlice } from "../../../Redux/reducers/responseSlice";
import {
  handleAddressChange,
  triggerSave,
} from "../../../Redux/actions/responseEventAction";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { useState } from "react";
import { countries } from "../../../const/countries";

type Props = {
  currentQuestion: QuestionSlice;
};

const AddressSave = ({ handleSave }: { handleSave: () => void }) => {
  const isPreview = useAppSelector((state) => !state.response.formEditable);
  const isFieldSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting
  );

  return (
    <Button
      disabled={!!isFieldSubmitting || isPreview}
      variant="contained"
      onClick={handleSave}
      sx={{ width: "fit-content", alignSelf: "flex-end" }}
    >
      Save
    </Button>
  );
};

const AddressField = ({ currentQuestion }: Props) => {
  const { id: studyId, surveySlug } = useParams();
  const dispatch = useAppDispatch();
  const [isLoadingZipcode, setIsLoadingZipcode] = useState(false);

  const isPreview = useAppSelector((state) => !state.response.formEditable);

  const { responses, properties } = currentQuestion;
  const currentResponse = responses?.[0]?.addressData;

  const handleChange = (key: string, value: any) => {
    dispatch(
      handleAddressChange({
        id: currentQuestion.id,
        value: value,
        key,
      })
    );
  };

  const handleSave = () => {
    if (
      (properties.city && !currentResponse?.city) ||
      (properties.state && !currentResponse?.state) ||
      (properties.country && !currentResponse?.country) ||
      (properties.zipcode && !currentResponse?.zipcode)
    ) {
      toastMessage(
        "warning",
        "Please fill all the required fields before saving."
      );
      return;
    }
    dispatch(
      triggerSave({
        studyId,
        id: currentQuestion.id,
        surveySlug,
      })
    );
  };

  const handleZipcode = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const zipcode = e.target.value;
    const showFetch =
      zipcode?.length >= 2 &&
      (properties?.country || properties?.state || properties?.city);
    if (showFetch) {
      setIsLoadingZipcode(true);
      try {
        const res = await http.get(`/data/zipcode?zipcode=${zipcode}`);
        const data = res?.data?.data;
        if (data) {
          if (properties.country && data.country) {
            dispatch(
              handleAddressChange({
                id: currentQuestion.id,
                value: data.country,
                key: "country",
              })
            );
          }
          if (properties.state && data.state) {
            dispatch(
              handleAddressChange({
                id: currentQuestion.id,
                value: data.state,
                key: "state",
              })
            );
          }
          if (properties.city && data.city) {
            dispatch(
              handleAddressChange({
                id: currentQuestion.id,
                value: data.city,
                key: "city",
              })
            );
          }
        }
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setIsLoadingZipcode(false);
      }
    }
  };

  return (
    <Stack
      sx={{
        pl: "60px",
        width: {
          xs: "100%",
          md: "80%",
        },
      }}
      gap={1}
    >
      {properties?.zipcode && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={500}>
            Zip Code
          </Typography>
          <TextField
            name="zipcode"
            fullWidth
            placeholder="Enter Zipcode"
            value={currentResponse?.zipcode || ""}
            onChange={(e) => handleChange("zipcode", e.target.value)}
            InputProps={{
              readOnly: isPreview,
              endAdornment: isLoadingZipcode ? (
                <CircularProgress size={20} />
              ) : null,
            }}
            onBlur={(e) => handleZipcode(e)}
          />
        </Stack>
      )}
      {properties?.city && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={500}>
            City/Town
          </Typography>
          <TextField
            name="city"
            fullWidth
            placeholder="City/Town"
            value={currentResponse?.city || ""}
            InputProps={{ readOnly: true }}
          />
        </Stack>
      )}
      {properties?.state && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={500}>
            State/Region
          </Typography>
          <TextField
            name="state"
            fullWidth
            placeholder="State/Region"
            value={currentResponse?.state || ""}
            InputProps={{ readOnly: true }}
          />
        </Stack>
      )}
      {properties?.country && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={500}>
            Country
          </Typography>
          <Select
            fullWidth
            value={currentResponse?.country || ""}
            onChange={(e) => handleChange("country", e.target.value)}
            readOnly={isPreview}
          >
            {countries.map((country) => (
              <MenuItem key={country.label} value={country.label}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      <AddressSave handleSave={handleSave} />
    </Stack>
  );
};
export default AddressField;
